import React, { useRef, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Formik, Form, FormikActions } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Flex,
  Input,
  Label,
  Button,
  Select,
  Toast,
  theme,
  FormMessage,
  Dropover,
  Link,
  Checkbox,
  Flow,
} from '@xometry/xometry_loft';
import {
  WWW_ORIGIN,
  ALLOWED_REDIRECTS,
  WORKCENTER_ORIGIN,
} from '../../../../config';
import {
  roleValues,
  intentionSelectValues,
  discoverySelectValues,
  getIntentionMappingForIntentionSelect,
} from '../../../../utils/constants';
import {
  UserAcceptTosMutationResult,
  UserAcceptTosMutationVariables,
  UserAcceptTosQuery,
} from '../../../../schema/generated/schema';
import {
  firstName,
  lastName,
  intention,
  role,
  requiredMessage,
} from '../../../../utils/validations';
import usersApi from '../../../../services/users';
import {
  completeFederatedUserRegistration,
  completeXometryUserRegistration,
  redirectUserOnSuccessfulRegistration,
  UpdateFederatedUserPayload,
} from './helpers';

const schema = yup.object().shape({
  firstName: firstName.required(requiredMessage),
  lastName: lastName.required(requiredMessage),
  intention: intention.required(requiredMessage),
  role: role.required(requiredMessage),
  terms: yup
    .boolean()
    .oneOf([true], 'Agree to the terms and conditions to create an account.'),
  businessSite: yup.string().optional(),
  xometryMarketing: yup.boolean(),
  thomasMarketing: yup.boolean(),
  discovery: yup.string(),
  discoveryOther: yup.string().when('discovery', {
    is: discoverySelectValues.other,
    then: yup.string().required('Please enter a value.'),
  }),
});
interface Props {
  userId: string | undefined;
  email: string | undefined;
}

interface FormValues extends UpdateFederatedUserPayload {
  terms: boolean;
}

const sitePrefix = 'https://';

const initialValues: FormValues = {
  xometryUserId: '',
  firstName: '',
  lastName: '',
  email: '',
  intention: '',
  role: '',
  businessSite: sitePrefix,
  terms: false,
  xometryMarketing: false,
  thomasMarketing: false,
  completingRegistration: false,
  discovery: '',
  discoveryOther: '',
  thomasOrigin: false,
};

const endsWithArray = (wordToSearch: string, arrayOfWords: []) => {
  let value = false;
  value = arrayOfWords.some((element: string) => {
    return wordToSearch.endsWith(element);
  });

  return value;
};

const RegistrationForm = ({ userId, email }: Props) => {
  const [companyName, setCompanyName] = useState(null);

  const [searchParams] = useSearchParams();

  // the invalid is if path is not present. Invalid is an ok default because it will default us to quoting home.
  const pathUrl = new URL(
    searchParams.get('path') || 'https://www.invalid.com'
  );

  let customerRedirectUrl: string;
  if (endsWithArray(pathUrl.hostname, ALLOWED_REDIRECTS)) {
    // searchParams.get(path) should never be empty at that this point
    customerRedirectUrl = searchParams.get('path') || '';
  } else {
    customerRedirectUrl = `${WWW_ORIGIN}/quoting/home/`;
  }

  const mbsy = searchParams.get('mbsy') || '';
  const segmentid = searchParams.get('segmentid') || '';
  const campaignid = searchParams.get('campaignid') || '';

  const [userApiError, setUserApiError] = useState('');
  const initialInputRef = useRef<HTMLInputElement>(null);
  initialValues.email = email || '';

  // The thomas URL param determines the origin of the user
  // true if the user originated from Thomas
  // false if the user originated from Xometry
  // set the boolean based on the value provided
  const thomasOrigin = searchParams.get('thomas') === 'true';

  const [userAcceptTos] = useMutation<
    UserAcceptTosMutationResult,
    UserAcceptTosMutationVariables
  >(UserAcceptTosQuery);

  useEffect(() => {
    // analytics call
    if (window.analytics) {
      window.analytics.page('Unified Registration');
      window.analytics.identify({
        userId: userId,
        traits: {
          id: userId,
        },
      });
    }

    // Set initial focus
    if (initialInputRef && initialInputRef.current) {
      initialInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const getCompanyName = async () => {
      const userById = await usersApi.getUser(userId).then((res) => res.data);
      const { company_name: companyName } = userById;
      setCompanyName(companyName);
    };
    if (userId) {
      getCompanyName();
    }
  }, [userId]);

  const handleRegisterUser = async (
    submittedValues: FormValues,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    let siteUrl = '';
    if (submittedValues.businessSite.length > sitePrefix.length) {
      siteUrl = submittedValues.businessSite;
    }
    // maps intention select value for intention to isRoleSupplier or isRoleBuyer
    // reassigns the value to the intention key
    const intentionValue = `${submittedValues.intention}`;
    const intention = getIntentionMappingForIntentionSelect(
      submittedValues.intention
    );

    const isPartnerSelected = intentionValue === intentionSelectValues.partner;
    const isWorkcenterSelected =
      intentionValue === intentionSelectValues.workcenter;

    const shouldRedirectToPartnerApplication =
      (isPartnerSelected || isWorkcenterSelected) &&
      customerRedirectUrl?.endsWith('/quoting/home');

    let partnerRedirectUrl = '';
    if (shouldRedirectToPartnerApplication) {
      const partnerApplicationPath = '/partner-application/application';
      partnerRedirectUrl = `${WORKCENTER_ORIGIN}${partnerApplicationPath}`;
    }

    const isFederatedUserUpdated = await completeFederatedUserRegistration({
      ...submittedValues,
      intention,
      xometryUserId: userId || '',
      role: roleValues[submittedValues.role as keyof typeof roleValues],
      businessSite: siteUrl,
      xometryMarketing: submittedValues.xometryMarketing || !thomasOrigin,
      thomasMarketing: submittedValues.thomasMarketing || thomasOrigin,
      thomasOrigin,
      completingRegistration: true,
      registrationIntention: intentionValue,
      mbsy,
      segmentid,
      campaignid,
    });

    let updatedXometryUser;
    if (!isFederatedUserUpdated) {
      updatedXometryUser = await completeXometryUserRegistration(
        userId || '',
        submittedValues.firstName,
        submittedValues.lastName,
        companyName,
        intentionValue,
        userAcceptTos,
        setUserApiError,
        mbsy,
        segmentid,
        campaignid
      );
    }

    if (
      isFederatedUserUpdated ||
      (updatedXometryUser && updatedXometryUser.first_name !== '')
    ) {
      // analytics
      if (window.analytics) {
        window.analytics.track('Create Account', {
          origin: 'Unified Registration',
          userId: userId,
          firstName: submittedValues.firstName,
          lastName: submittedValues.lastName,
          companyName,
          discovery: submittedValues.discovery,
          discoveryOther:
            submittedValues.discovery === discoverySelectValues.other &&
            submittedValues.discoveryOther
              ? submittedValues.discoveryOther
              : '',
        });
        window.analytics.identify({
          userId: userId,
          traits: {
            id: userId,
            firstName: submittedValues.firstName,
            lastName: submittedValues.lastName,
            companyName,
          },
        });
      }
      redirectUserOnSuccessfulRegistration(
        intention,
        partnerRedirectUrl || customerRedirectUrl,
        thomasOrigin
      );
    } else {
      setSubmitting(false);
    }
  };

  return (
    <Box
      paddingX={[theme.space[5], theme.space[10]]}
      fontFamily={theme.fonts[0]}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={(
          values: FormValues,
          { setSubmitting }: FormikActions<FormValues>
        ) => {
          setUserApiError('');
          setSubmitting(true);
          handleRegisterUser(values, setSubmitting);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form>
            <Flex
              flexDirection={['column', 'row']}
              marginBottom={[theme.space[3]]}
              justifyContent={['center', 'space-between']}
              color={theme.colors.darkBlues[90]}
            >
              <Box
                marginBottom={[theme.space[3], theme.space[0]]}
                width="100%"
                marginRight={[theme.space[0], theme.space[2]]}
              >
                <Label
                  htmlFor="firstName"
                  lineHeight={theme.lineHeights[2]}
                  color={theme.colors.darkBlues[90]}
                >
                  First Name
                </Label>
                <Input
                  className="pii-encrypt"
                  ref={initialInputRef}
                  width="100%"
                  id="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={Boolean(touched.firstName && errors.firstName)}
                />
                {Boolean(touched.firstName && errors.firstName) && (
                  <FormMessage invalid>{errors.firstName}</FormMessage>
                )}
              </Box>
              <Box width="100%" marginLeft={[theme.space[0], theme.space[2]]}>
                <Label
                  htmlFor="lastName"
                  lineHeight={theme.lineHeights[2]}
                  color={theme.colors.darkBlues[90]}
                >
                  Last Name
                </Label>
                <Input
                  className="pii-encrypt"
                  width="100%"
                  id="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={Boolean(touched.lastName && errors.lastName)}
                />
                {Boolean(touched.lastName && errors.lastName) && (
                  <FormMessage invalid>{errors.lastName}</FormMessage>
                )}
              </Box>
            </Flex>
            <Box marginBottom={[theme.space[5]]}>
              <Label htmlFor="email">Email</Label>
              <Input
                className="pii-encrypt"
                width="100%"
                id="email"
                disabled
                defaultValue={email}
              />
            </Box>
            <Flex flexDirection="column" marginBottom={[theme.space[4]]}>
              <Flex flexDirection="row" alignItems="center">
                <Label htmlFor="intention">What would you like to do?</Label>
                <Flex
                  fontSize={theme.fontSizes[1]}
                  marginLeft={theme.space[1]}
                  marginBottom={theme.space[1]}
                >
                  <Dropover alignment="center" placement="top">
                    <Dropover.Trigger>
                      <Box color={theme.colors.blues[100]}>
                        <i className="far fa-info-circle" />
                      </Box>
                    </Dropover.Trigger>
                    <Dropover.Content>
                      <Box
                        width="400px"
                        fontWeight={theme.fontWeights.regular}
                        fontSize={theme.fontSizes[0]}
                        lineHeight={theme.lineHeights[2]}
                      >
                        Select one of the following options from the menu to
                        customize your experience with Xometry and Thomas. All
                        options will still be available to you after
                        registration.
                      </Box>
                    </Dropover.Content>
                  </Dropover>
                </Flex>
              </Flex>

              {/* Intention Role */}
              <Select
                id="intention"
                data-testid="intention"
                width="100%"
                value={values.intention}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={Boolean(touched.intention && errors.intention)}
              >
                <option value="" disabled>
                  Choose an option...
                </option>
                <optgroup label="Xometry">
                  <option
                    key={`${intentionSelectValues.quotes}-xometry`}
                    value={intentionSelectValues.quotes}
                  >
                    Get Instant Quotes
                  </option>
                  <option
                    key={`${intentionSelectValues.partner}-xom-man`}
                    value={intentionSelectValues.partner}
                  >
                    Get Work as a Manufacturing Partner
                  </option>
                  <option
                    key={`${intentionSelectValues.workcenter}-xom-work`}
                    value={intentionSelectValues.workcenter}
                  >
                    Use the Workcenter Manufacturing Execution System (MES)
                  </option>
                </optgroup>
                <optgroup label="Thomas">
                  <option
                    key={`${intentionSelectValues.generateLeads}-thomas`}
                    value={intentionSelectValues.generateLeads}
                  >
                    Generate Business Leads
                  </option>
                  <option
                    key={`${intentionSelectValues.findSuppliers}-thomas`}
                    value={intentionSelectValues.findSuppliers}
                  >
                    Find Suppliers
                  </option>
                </optgroup>
              </Select>
              {Boolean(touched.intention && errors.intention) && (
                <FormMessage invalid>{errors.intention}</FormMessage>
              )}
            </Flex>
            <Flex flexDirection="column" marginBottom={[theme.space[4]]}>
              <Label htmlFor="role">Job Role</Label>
              <Select
                id="role"
                data-testid="role"
                width="100%"
                value={values.role}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={Boolean(touched.role && errors.role)}
              >
                <option value="">Choose an option...</option>
                {Object.keys(roleValues).map((value) => (
                  <option key={value} value={value}>
                    {roleValues[value as keyof typeof roleValues]}
                  </option>
                ))}
              </Select>
              {Boolean(touched.role && errors.role) && (
                <FormMessage invalid>{errors.role}</FormMessage>
              )}
            </Flex>
            <Box marginBottom={[theme.space[6]]}>
              <Label htmlFor="business-site">Business Website</Label>
              <Input
                className="pii-encrypt"
                width="100%"
                id="business-site"
                type="text"
                defaultValue={values.businessSite}
                invalid={Boolean(touched.businessSite && errors.businessSite)}
                onChange={(e: any) => {
                  const input = e.target.value;
                  values.businessSite = e.target.value =
                    sitePrefix + input.substr(sitePrefix.length);
                }}
              />
              {Boolean(touched.businessSite && errors.businessSite) && (
                <FormMessage invalid>{errors.businessSite}</FormMessage>
              )}
            </Box>
            <Flex flexDirection="column" marginBottom={[theme.space[4]]}>
              <Flex flexDirection="row" alignItems="center">
                <Label htmlFor="discovery">
                  How did you learn about Xometry?
                </Label>
              </Flex>

              {/* Discovery Answer */}
              <Select
                id="discovery"
                data-testid="discovery"
                width="100%"
                value={values.discovery}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={Boolean(touched.discovery && errors.discovery)}
              >
                <option value="" disabled>
                  Choose an option...
                </option>
                <option
                  key={`${discoverySelectValues.search}`}
                  value={discoverySelectValues.search}
                >
                  Online Search (Google, Bing, etc.)
                </option>
                <option
                  key={`${discoverySelectValues.social}`}
                  value={discoverySelectValues.social}
                >
                  Social Media (Facebook, LinkedIn, Instagram, Youtube, etc.)
                </option>
                <option
                  key={`${discoverySelectValues.referral}`}
                  value={discoverySelectValues.referral}
                >
                  Referral from Another Manufactuer or Customer
                </option>
                <option
                  key={`${discoverySelectValues.xometryOutreach}`}
                  value={discoverySelectValues.xometryOutreach}
                >
                  Xometry Email or Outreach Call
                </option>
                <option
                  key={`${discoverySelectValues.thomas}`}
                  value={discoverySelectValues.thomas}
                >
                  Thomasnet
                </option>
                <option
                  key={`${discoverySelectValues.tradeshow}`}
                  value={discoverySelectValues.tradeshow}
                >
                  Industry Tradeshow or Event (e.g. IMTS, FABTECH, etc.)
                </option>
                <option
                  key={`${discoverySelectValues.podcast}`}
                  value={discoverySelectValues.podcast}
                >
                  Podcast or Industry Webinar
                </option>
                <option
                  key={`${discoverySelectValues.blog}`}
                  value={discoverySelectValues.blog}
                >
                  Blog Post or Online Article
                </option>
                <option
                  key={`${discoverySelectValues.manufacturingAssociation}`}
                  value={discoverySelectValues.manufacturingAssociation}
                >
                  Manufacturing Association or Group
                </option>
                <option
                  key={`${discoverySelectValues.other}`}
                  value={discoverySelectValues.other}
                >
                  Other (please specify)
                </option>
              </Select>
              {values.discovery === discoverySelectValues.other && (
                <Input
                  className="pii-encrypt"
                  marginTop={theme.space[1]}
                  width="100%"
                  id="discoveryOther"
                  type="text"
                  defaultValue={values.discoveryOther}
                  invalid={Boolean(
                    touched.discoveryOther && errors.discoveryOther
                  )}
                  onChange={handleChange}
                />
              )}
              {Boolean(touched.discovery && errors.discovery) && (
                <FormMessage invalid>{errors.discovery}</FormMessage>
              )}
              {Boolean(
                touched.discovery &&
                  touched.discoveryOther &&
                  values.discovery === discoverySelectValues.other
              ) && <FormMessage invalid>{errors.discoveryOther}</FormMessage>}
            </Flex>
            {Boolean(touched.terms && errors.terms) && (
              <Alert kind={theme.kind.danger} marginBottom={theme.space[6]}>
                <>{errors.terms}</>
              </Alert>
            )}
            <Button
              type="submit"
              width="100%"
              height={theme.space[10]}
              paddingY={theme.space[2]}
              kind={theme.kind.primary}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Flow scale="panel" kind="disabled" />
              ) : (
                <>
                  {values.intention === 'isRoleSupplier'
                    ? 'Continue Registration'
                    : 'Create Account'}
                </>
              )}
            </Button>
            <Flex flexDirection="column" paddingY={theme.space[5]}>
              {thomasOrigin ? (
                <Flex alignItems="flex-start">
                  <Box paddingRight={theme.space[1]}>
                    <Checkbox
                      id="xometryMarketing"
                      checked={values.xometryMarketing}
                      scale="small"
                      enterKeyHint="next"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Box>
                  <Box
                    fontFamily={theme.fonts[0]}
                    fontSize={theme.fontSizes[0]}
                    color={theme.colors.darkBlues[100]}
                  >
                    <Label
                      htmlFor="xometryMarketing"
                      marginTop={theme.space[1]}
                      fontWeight={theme.fontWeights.regular}
                    >
                      I would like to receive Xometry promotional offers.
                    </Label>
                  </Box>
                </Flex>
              ) : (
                <Flex alignItems="flex-start">
                  <Box paddingRight={theme.space[1]}>
                    <Checkbox
                      id="thomasMarketing"
                      checked={values.thomasMarketing}
                      scale="small"
                      enterKeyHint="next"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Box>
                  <Box
                    fontFamily={theme.fonts[0]}
                    fontSize={theme.fontSizes[0]}
                    color={theme.colors.darkBlues[100]}
                  >
                    <Label
                      htmlFor="thomasMarketing"
                      marginTop={theme.space[1]}
                      fontWeight={theme.fontWeights.regular}
                    >
                      I would like to receive Thomas promotional offers.
                    </Label>
                  </Box>
                </Flex>
              )}
              <Flex alignItems="flex-start">
                <Box paddingRight={theme.space[1]}>
                  <Checkbox
                    id="terms"
                    checked={values.terms}
                    scale="small"
                    enterKeyHint="next"
                    invalid={Boolean(touched.terms && errors.terms)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Box>
                <Box
                  fontFamily={theme.fonts[0]}
                  fontSize={theme.fontSizes[0]}
                  color={theme.colors.darkBlues[100]}
                >
                  <Label
                    htmlFor="terms"
                    marginTop={theme.space[1]}
                    fontWeight={theme.fontWeights.regular}
                  >
                    <Box>
                      I agree to Xometry{' '}
                      <Link
                        data-testid="xometry-tos"
                        href="https://xometry-marketing.cdn.prismic.io/xometry-marketing/895c7155-a7e9-4299-bc0a-26f78a02223d_Terms+%26+Conditions+04_04_2017.pdf"
                        target="_blank"
                      >
                        Terms and Conditions
                      </Link>{' '}
                      and{' '}
                      <Link
                        data-testid="xometry-privacy"
                        href="https://xometry-marketing.cdn.prismic.io/xometry-marketing/46b8d3fc-8a69-4a3a-ad40-c2c10a83307d_Xometry+Privacy+Policy_05272022.pdf"
                        target="_blank"
                      >
                        Privacy Policy
                      </Link>{' '}
                      and the Thomas{' '}
                      <Link
                        data-testid="thomas-tos"
                        href="https://www.thomasnet.com/terms.html"
                        target="_blank"
                      >
                        Terms and Conditions
                      </Link>{' '}
                      and{' '}
                      <Link
                        data-testid="thomas-privacy"
                        href="https://www.thomasnet.com/privacy.html"
                        target="_blank"
                      >
                        Privacy Policy
                      </Link>
                    </Box>
                  </Label>
                </Box>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
      {userApiError && (
        <Toast
          marginTop={theme.space[4]}
          kind={theme.kind.danger}
          isOpen={userApiError !== ''}
          hideClose
          onClose={() => null}
        >
          {userApiError}
        </Toast>
      )}
    </Box>
  );
};

export default RegistrationForm;
